import React, { useState, useEffect } from 'react';
import SuggestedName from './DocumentNaming/SuggestedName';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFilePdf, 
    faFileWord, 
    faFileExcel, 
    faFileImage, 
    faFile,
    faFileCode,
    faFileArchive,
    faDownload,
    faTrash,
    faChevronDown,
    faEye
} from '@fortawesome/free-solid-svg-icons';
import './DeliveryDocumentComponents.css';

// Only a PDF can be opened in the in-app viewer, so only a PDF gets the
// "open here" button. Offering it on a Word file or a photo would be a button
// that always fails.
const isPdfFile = (filename) => {
    if (!filename) return false;
    return String(filename).split('.').pop().toLowerCase() === 'pdf';
};

export const DeliveryDocumentGroup = ({ 
    documentType, 
    categoryColor,
    documents, 
    onDownload, 
    onPreview,
    onDelete, 
    getDocumentInfo, 
    formatDate, 
    formatFileSize,
    getEmployeeInfoByUserId
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const docCount = documents.length;
    
    return (
        <div className="delivery-document-group" style={{ borderColor: categoryColor ? categoryColor + '40' : undefined }}>
            <div 
                className="delivery-group-header"
                style={categoryColor ? {
                    background: `linear-gradient(135deg, ${categoryColor}, ${categoryColor}dd)`,
                    color: 'white'
                } : undefined}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="delivery-group-title-wrapper">
                    <span>{documentType}</span>
                    <span className="delivery-group-doc-count" style={{
                        backgroundColor: categoryColor ? 'rgba(255,255,255,0.25)' : 'rgba(74,107,37,0.1)',
                        color: categoryColor ? 'white' : '#4a6b25'
                    }}>
                        {docCount}
                    </span>
                </div>
                <FontAwesomeIcon 
                    icon={faChevronDown} 
                    style={{ 
                        fontSize: '1rem', 
                        color: categoryColor ? 'rgba(255,255,255,0.8)' : '#4a6b25',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}
                />
            </div>
            
            {isExpanded && docCount > 0 && (
                <div className="delivery-group-documents">
                    {documents.map(document => (
                        <DeliveryDocumentItem
                            key={document.Id}
                            document={document}
                            onDownload={onDownload}
                            onPreview={onPreview}
                            onDelete={onDelete}
                            getDocumentInfo={getDocumentInfo}
                            formatDate={formatDate}
                            formatFileSize={formatFileSize}
                            getEmployeeInfoByUserId={getEmployeeInfoByUserId}
                        />
                    ))}
                </div>
            )}

            {isExpanded && docCount === 0 && (
                <div className="delivery-group-empty">
                    No documents uploaded
                </div>
            )}
        </div>
    );
};

/*
// PREVIOUS COMPACT VERTICAL LAYOUT (Commented for reference)
// This was the first solution before implementing ultra-compact layout
// Kept for rollback purposes if needed
*/

// Get file icon based on file extension - matching My Files pattern
const getFileIcon = (filename) => {
    if (!filename) return faFile;
    const extension = filename.split('.').pop().toLowerCase();
    
    if (extension === 'pdf') return faFilePdf;
    if (['doc', 'docx'].includes(extension)) return faFileWord;
    if (['xls', 'xlsx'].includes(extension)) return faFileExcel;
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'webp'].includes(extension)) return faFileImage;
    if (['html', 'css', 'js', 'jsx', 'ts', 'tsx', 'json', 'xml', 'md', 'adoc'].includes(extension)) return faFileCode;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) return faFileArchive;
    
    return faFile;
};

// Individual delivery document item - ULTRA COMPACT LAYOUT
export const DeliveryDocumentItem = ({ 
    document, 
    onDownload, 
    onPreview,
    onDelete, 
    getDocumentInfo, 
    formatDate, 
    formatFileSize, 
    getEmployeeInfoByUserId 
}) => {
    const [employeeInfo, setEmployeeInfo] = useState(null);
    const [loadingEmployee, setLoadingEmployee] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    
    const docInfo = getDocumentInfo(document);
    
    // Fetch employee info when component mounts
    useEffect(() => {
        const fetchEmployeeInfo = async () => {
            if (!docInfo.employeeId) return;
            
            setLoadingEmployee(true);
            try {
                const empInfo = await getEmployeeInfoByUserId(docInfo.employeeId);
                setEmployeeInfo(empInfo);
            } catch (error) {
                console.error('Error fetching employee info for document:', error);
            } finally {
                setLoadingEmployee(false);
            }
        };
        
        fetchEmployeeInfo();
    }, [docInfo.employeeId, getEmployeeInfoByUserId]);
    
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.5rem 0.75rem',
            marginBottom: '0.25rem',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            fontSize: '0.75rem',
            minHeight: '3.5rem',
            transition: 'all 0.2s ease',
            cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f8fafc';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.boxShadow = 'none';
        }}
        >
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <FontAwesomeIcon 
                    icon={getFileIcon(docInfo.filename)} 
                    style={{ 
                        fontSize: '2rem', 
                        color: '#4a6b25',
                        flexShrink: 0
                    }} 
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{ 
                            fontWeight: '600', 
                            color: '#374151',
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginBottom: '0.25rem'
                        }}
                        title={docInfo.filename}
                    >
                        {docInfo.displayName || docInfo.filename}
                    </div>
                    <SuggestedName row={docInfo.nameRow} onChanged={docInfo.onNameRow} />
                    
                    <div style={{ 
                        fontSize: '0.75rem', 
                        color: '#9ca3af',
                        lineHeight: '1.3',
                        fontWeight: '500'
                    }}>
                        {formatDate(docInfo.dateUploaded)} • {formatFileSize(docInfo.fileSize)}
                    </div>
                </div>
                
                {/* Employee info as compact badge with tooltip */}
                <div 
                    style={{ 
                        position: 'relative',
                        display: 'inline-block'
                    }}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    <div style={{
                        fontSize: '0.7rem',
                        color: '#6b7280',
                        padding: '0.125rem 0.25rem',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '2px',
                        cursor: 'help',
                        whiteSpace: 'nowrap'
                    }}>
                        {loadingEmployee ? '...' : employeeInfo ? 
                            `${employeeInfo.first_name} ${employeeInfo.last_name}` : 
                            'Unknown'
                        }
                    </div>
                    
                    {/* Tooltip */}
                    {showTooltip && employeeInfo && (
                        <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#1f2937',
                            color: 'white',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            zIndex: 10000,
                            whiteSpace: 'nowrap',
                            marginBottom: '0.25rem',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}>
                            <div style={{ fontWeight: '500' }}>
                                {employeeInfo.first_name} {employeeInfo.last_name}
                            </div>
                            {employeeInfo.position && (
                                <div style={{ color: '#d1d5db', fontSize: '0.65rem' }}>
                                    {employeeInfo.position}
                                    {employeeInfo.department && ` • ${employeeInfo.department}`}
                                </div>
                            )}
                            <div style={{ color: '#d1d5db', fontSize: '0.65rem' }}>
                                ID: {employeeInfo.employee_id}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '0.75rem', flexShrink: 0 }}>
                {/* "Open it here". Until now the only thing a document row
                    could do was save the file to disk, which is why checking a
                    delivery against its paperwork meant leaving the screen.
                    Same request as the download below -- same route, same
                    permission gate -- just shown instead of saved. */}
                {onPreview && isPdfFile(docInfo.filename) && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onPreview(docInfo.documentId, docInfo.displayName || docInfo.filename);
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '2rem',
                            height: '2rem',
                            border: 'none',
                            background: 'transparent',
                            color: '#6b7280',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            borderRadius: '6px',
                            fontSize: '0.875rem'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#eef3e6';
                            e.currentTarget.style.color = '#4a6b25';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = '#6b7280';
                        }}
                        title="Open document here"
                        aria-label={`Open ${docInfo.displayName || docInfo.filename} here`}
                    >
                        <FontAwesomeIcon icon={faEye} />
                    </button>
                )}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDownload(docInfo.documentId, docInfo.filename);
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '2rem',
                        height: '2rem',
                        border: 'none',
                        background: 'transparent',
                        color: '#6b7280',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.color = '#4a6b25';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#6b7280';
                    }}
                    title="Download document"
                >
                    <FontAwesomeIcon icon={faDownload} />
                </button>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(docInfo.documentId, docInfo.filename);
                    }}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '2rem',
                        height: '2rem',
                        border: 'none',
                        background: 'transparent',
                        color: '#6b7280',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fee2e2';
                        e.currentTarget.style.color = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#6b7280';
                    }}
                    title="Delete document"
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>
            </div>
        </div>
    );
}; 